/** What the user changed in one cell: a lead scoring to apply, or `null` to clear it. */
export type PendingAssignment = { criteriaId: number } | null;

/** The lead scoring the server currently reports for a cell. */
export interface ServerAssignment {
	id: number;
	name: string;
	ccpId: number;
}

export interface SaveSegmentAssignmentsDeps {
	/** Assigns one lead scoring to a cell, replacing whatever was connected to it. */
	assign: (payload: {
		scoreCompanyCategoryId?: number;
		scorePersonaCategoryId: number;
		scoreCriteriaId: number;
		investor?: string;
	}) => Promise<unknown>;
	/** Removes a single connection by its `score_criteria_company` id. */
	remove: (ccpId: number) => Promise<unknown>;
	getServerAssignment: (companyId: number, personaId: number) => ServerAssignment | null;
	getServerAssignmentForInvestor: (
		investorName: string,
		personaId: number
	) => ServerAssignment | null;
}

/** Splits a pending-assignment map key back into the cell it addresses. */
const parseCellKey = (key: string) => {
	if (key.startsWith("investor|")) {
		const [, investorName, personaId] = key.split("|");

		return { investorName, personaId: Number(personaId), companyId: null };
	}

	const [companyId, personaId] = key.split("-").map(Number);

	return { investorName: undefined, personaId, companyId };
};

/**
 * Writes the overview's pending cell changes to the server, one cell at a time.
 *
 * Assigning goes through the single "assign" endpoint, which clears the cell and
 * connects the chosen scoring in one server-side step. The page used to delete the
 * one connection it happened to know about and then create another, which could not
 * converge on a cell holding more than one row and instead added yet another
 * (PREC-3946). Clearing a cell is still a plain delete of that row.
 */
export const saveSegmentAssignments = async (
	pendingAssignments: Map<string, PendingAssignment>,
	{
		assign,
		remove,
		getServerAssignment,
		getServerAssignmentForInvestor,
	}: SaveSegmentAssignmentsDeps
): Promise<void> => {
	for (const [key, pending] of pendingAssignments) {
		const { investorName, personaId, companyId } = parseCellKey(key);

		const server = investorName
			? getServerAssignmentForInvestor(investorName, personaId)
			: getServerAssignment(companyId!, personaId);

		if (pending) {
			if (server && server.id === pending.criteriaId) {
				continue;
			}

			await assign({
				scoreCompanyCategoryId: companyId ?? undefined,
				scorePersonaCategoryId: personaId,
				scoreCriteriaId: pending.criteriaId,
				investor: investorName,
			});

			continue;
		}

		if (server) {
			await remove(server.ccpId);
		}
	}
};
